package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.ComponentAst
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError

/**
 * 组件收集阶段：把所有 [ComponentAst] 注册进 [ComponentRegistry]，并完成结构性校验。
 *
 * 校验项：
 * - DSL_E304：单个组件 root 树内存在 2+ 个匿名 children 占位
 * - DSL_E323：单个组件 root 树内同名具名插槽（`children "header"`）重复声明
 * - DSL_E308：注册时同名组件已存在
 */
class ComponentCollectorPass(
    private val registry: ComponentRegistry,
    private val diag: DiagnosticCollector,
) {

    fun collect(components: List<ComponentAst>) {
        for (c in components) {
            collectOne(c)
        }
    }

    /**
     * M6-d：注册一组内联 component（来自单个 screen）。
     *
     * 与跨文件注册的差异：
     * - 与已注册（跨文件）同名 → [DslError.E710_INLINE_COMPONENT_NAME_CONFLICT_CROSS_FILE]
     * - 同一调用内重复 → [DslError.E712_INLINE_COMPONENT_DUPLICATE_NAME]
     *
     * 必须在 [collect]（跨文件批量注册）之后调用，以便 E710 检测生效。
     */
    fun collectInline(inlineComponents: List<ComponentAst>) {
        val seen = mutableSetOf<String>()
        for (c in inlineComponents) {
            if (!seen.add(c.id)) {
                diag.error(DslError.E712_INLINE_COMPONENT_DUPLICATE_NAME, c.position, c.id)
                continue
            }
            if (registry.contains(c.id)) {
                diag.error(DslError.E710_INLINE_COMPONENT_NAME_CONFLICT_CROSS_FILE, c.position, c.id)
                continue
            }
            collectOne(c)
        }
    }

    private fun collectOne(c: ComponentAst) {
        if (registry.contains(c.id)) {
            diag.error(DslError.E308_DUPLICATE_COMPONENT, c.position, c.id)
            return
        }
        // 按 slotName 分桶计数：匿名洞（key=null）>1 → E304；同名具名洞 >1 → E323。
        val slotCounts = countChildrenSlots(c.root)
        val anonymousCount = slotCounts[null] ?: 0
        if (anonymousCount > 1) {
            diag.error(DslError.E304_MULTIPLE_CHILDREN_SLOTS, c.position, c.id, anonymousCount)
            return
        }
        val duplicateNamed = slotCounts.entries.firstOrNull { it.key != null && it.value > 1 }
        if (duplicateNamed != null) {
            diag.error(DslError.E323_DUPLICATE_NAMED_SLOT, c.position, c.id, duplicateNamed.key!!, duplicateNamed.value)
            return
        }
        registry.put(c.id, c)
    }

    /** 按 slotName 分桶统计 children 占位数量：key=null 为匿名洞，key=name 为具名洞。 */
    private fun countChildrenSlots(root: ElementAst): Map<String?, Int> {
        val counts = linkedMapOf<String?, Int>()
        fun walk(e: ElementAst) {
            if (e.isChildrenSlot) {
                counts[e.slotName] = (counts[e.slotName] ?: 0) + 1
                return
            }
            for (child in e.children) walk(child)
        }
        walk(root)
        return counts
    }
}
